import { useState } from "react";
import { ShieldCheck, Loader2, LogOut } from "lucide-react";
import { useUser } from "@/context/UserContext";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const AvgConsent = () => {
  const { user, acceptTerms, logout } = useUser();
  const { t } = useLang();
  const [voorwaarden, setVoorwaarden] = useState(false);
  const [aiBesef, setAiBesef] = useState(false);
  const [busy, setBusy] = useState(false);

  const bevestig = async () => {
    if (!voorwaarden) { toast.error("Je moet akkoord gaan met de Algemene Voorwaarden en de Privacyverklaring."); return; }
    if (!aiBesef) { toast.error("Bevestig dat je begrijpt dat Zoetwaren Kompas een AI-ondersteunde testomgeving is."); return; }
    setBusy(true);
    try {
      await acceptTerms();
      toast.success("Bedankt. Je hebt nu toegang tot Zoetwaren Kompas.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Er ging iets mis. Probeer het opnieuw.");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-6">
      <div data-testid="avg-consent" className="max-w-md w-full bg-white border border-slate-200 rounded-2xl p-8 shadow-sm animate-fade-up">
        <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center mb-4">
          <ShieldCheck className="w-6 h-6 text-blue-600" />
        </div>
        <h2 className="font-heading text-2xl font-bold text-slate-900">{t("avg.title")}</h2>
        <p className="text-sm text-slate-500 mt-2">
          Welkom{user?.naam ? `, ${user.naam}` : ""}. Voordat je Zoetwaren Kompas gebruikt, vragen we je akkoord te gaan met onze voorwaarden. Bij een nieuwe versie van de documenten vragen we dit opnieuw.
        </p>
        <div className="mt-6 space-y-3">
          <label className="flex items-start gap-2 text-sm text-slate-600 cursor-pointer">
            <Checkbox data-testid="avg-voorwaarden" checked={voorwaarden} onCheckedChange={setVoorwaarden} className="mt-0.5" />
            <span>Ik heb de{" "}
              <a href="/voorwaarden" target="_blank" rel="noopener noreferrer" className="text-slate-900 font-medium underline">Algemene Voorwaarden</a>{" "}
              en de{" "}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-slate-900 font-medium underline">Privacyverklaring</a>{" "}
              gelezen en ga hiermee akkoord.
            </span>
          </label>
          <label className="flex items-start gap-2 text-sm text-slate-600 cursor-pointer">
            <Checkbox data-testid="avg-ai-besef" checked={aiBesef} onCheckedChange={setAiBesef} className="mt-0.5" />
            <span>Ik begrijp dat Zoetwaren Kompas een AI-ondersteunde testomgeving is en dat antwoorden onjuist of onvolledig kunnen zijn.</span>
          </label>
        </div>
        <Button data-testid="avg-bevestig" onClick={bevestig} disabled={busy} className="w-full mt-6 bg-slate-900 h-11">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
          {t("avg.confirm")}
        </Button>
        <button data-testid="avg-logout" onClick={logout} className="w-full mt-3 text-sm text-slate-500 hover:text-slate-900 inline-flex items-center justify-center gap-1">
          <LogOut className="w-4 h-4" /> {t("avg.logout")}
        </button>
      </div>
    </div>
  );
};

export default AvgConsent;
